import { measureGeneration } from '~/server/static-metrics'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FaFileAlt } from 'react-icons/fa'
import InfiniteScroll from 'react-infinite-scroller'
import { ArticleList } from 'web_core/assets/article'
import { HeadProps } from 'web_core/assets/Head'
import { MathJax } from 'web_core/assets/mathdown/mathjax'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESArticle } from 'web_core/elastic_search'
import {
  Category,
  categoryConverter,
  categoryRef,
  fb,
  getDoc,
} from 'web_core/firebase'
import { useESArticles } from 'web_core/hooks'
import {
  deserialize,
  deserializeArray,
  serialize,
  Serialized,
} from 'web_core/utils'
import { addLocaleProps } from 'web_core/utils/static_props'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { Heading } from '~/components/Utils/Heading'
import { useLayoutProperty } from '~/context/layout_property'
import {
  CategoryPageParams,
  getCategoryPageStaticPaths,
} from '~/static_props/category'
import {
  getCategoriesStaticProps,
  getPopularProfilesStaticProps,
  LayoutStaticProps,
} from '~/static_props/layout'
import { articleCategoryBreadcrumb } from '~/utils'

type Props = {
  articles: Serialized<ESArticle>[]
  category: Serialized<Category>
  seed: number
} & LayoutStaticProps

export const getStaticPaths = getCategoryPageStaticPaths

export const getStaticProps: GetStaticProps<
  Props,
  CategoryPageParams
> = async ({ locale = 'ja', params }) =>
  measureGeneration('category_articles', async (measure) => {
    const seed = Math.random()
    if (!params) throw new Error('Missing params.')

    const { data } = await measure(() =>
      fb.call('search')({
        index: 'articles',
        params: {
          locale: 'ja',
          seed,
          type: 'recommendation',
          category_id: params.id,
        },
      })
    )
    if (data.index !== 'articles') {
      throw new Error('Unexpected response.')
    }

    const categorySnapshot = await measure(() => getDoc(categoryRef(params.id)))
    const category = categorySnapshot.data()
    if (!category) {
      throw new Error('category is not found.')
    }

    const result = {
      props: {
        articles: data.result,
        category: serialize(category),
        categories: await measure(() => getCategoriesStaticProps()),
        popularProfiles: await measure(() => getPopularProfilesStaticProps()),
        seed,
      },
      revalidate: 10,
    }
    return addLocaleProps(result, locale)
  })

type View = 'ranking' | 'timeline' | 'recommendation'

type ContentsMenuProps = {
  category: Category
  view: View
  setView: Dispatch<SetStateAction<View>>
}

const ContentsMenu = ({ category, view, setView }: ContentsMenuProps) => {
  const { headerHeight } = useLayoutProperty()
  return (
    <div
      className="position-sticky bg-white pt-2 pb-2 mb-2 mb-md-4"
      style={{ top: headerHeight }}
    >
      <div className="d-flex justify-content-between gap-4">
        <ContentNavSelector
          variant="pills"
          active="articles"
          link={`/categories/${category.id}/[contents]`}
          className="flex-shrink-0 fs-7 fs-md-6"
        />
        <Form.Select
          onChange={(e) => setView(e.currentTarget.value as View)}
          style={{ maxWidth: 300 }}
        >
          <option value={'recommendation'} selected={view === 'recommendation'}>
            おすすめ順
          </option>
          <option value={'ranking'} selected={view === 'ranking'}>
            評価順
          </option>
          <option value={'timeline'} selected={view === 'timeline'}>
            作成日順
          </option>
        </Form.Select>
      </div>
    </div>
  )
}

const Page: NextPage<Props> = ({
  category: staticCategory,
  articles: staticArticles,
  categories,
  popularProfiles,
  seed,
}) => {
  const { i18n } = useTranslation()
  const lang = i18n.language

  const category = deserialize<Category>(staticCategory, {
    ref: categoryConverter,
  })
  const [view, setView] = useState<View>('recommendation')
  const { articles, loadMore, isReachingEnd, isLoadingMore } = useESArticles(
    view === 'recommendation'
      ? {
          locale: 'ja',
          type: 'recommendation',
          seed,
          category_id: category.id,
        }
      : {
          locale: 'ja',
          type: 'query',
          category_ids: [category.id],
          order: view === 'timeline' ? 'created_at desc' : 'score desc',
        },
    deserializeArray(staticArticles, {})
  )

  const head: HeadProps = {
    title: category.localized_name[lang],
    description: category.localized_summary[lang],
    keyword: category.localized_name[lang],
  }

  return (
    <DefaultLayout
      head={head}
      navLinks={{ pc: true, tablet: true, sp: true }}
      categoriesMenu={{ pc: true, sp: true, activeId: category.id }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      leftSidebar
      rightSideBar
    >
      {articleCategoryBreadcrumb(category, lang).toReactNode()}
      <Heading
        title={category.localized_name[lang]}
        icon={FaFileAlt}
        className="mb-0"
      />
      {category.localized_summary[lang] && (
        <MathJax className="mt-2 mb-4">
          {category.localized_summary[lang]}
        </MathJax>
      )}
      <ContentsMenu category={category} view={view} setView={setView} />
      <InfiniteScroll
        loadMore={async () => {
          if (isLoadingMore === false && isReachingEnd === false)
            await loadMore()
        }}
        hasMore={isReachingEnd === false}
        loader={<LoadingSpinner />}
      >
        {articles && (
          <ArticleList
            articles={articles}
            className="grid g-cols-1 g-lg-cols-2"
          />
        )}
      </InfiniteScroll>
    </DefaultLayout>
  )
}

export default Page
